/**
 * STEMS — instrument-true analysis. Two ways a set of separated stems reaches the analysers:
 *   the STEM SET   — pre-separated files the user dropped (audioSession.loadStems builds their graph and
 *                    hands the per-stem analysers here: setStemSet);
 *   the MUTED PARALLEL STEM SESSION — the in-browser separation of a dropped file (autoSeparate, behind a
 *                    dev flag — see audioSession.loadFile), played silently beside the file.
 * Either way readStems() fuses them per frame into the voices the MusicalFrame carries.
 */
import type { StemId, StemInput, StemVoicesFrame, StemVoicer, StereoAnalyser } from '@syqel/engine'
import { Engine } from '@syqel/ota-client'
import { surface, track } from '../analytics'
import { separateFile, type SeparatedStems } from '../separator'
import { closeCtx } from './graph'

// THE STEM SESSION — N pre-separated instrument files played sample-locked on one
// AudioContext; each stem gets its own analyser pair, the master mix feeds the main
// `stereo` analyser (so the voice-based MusicalFrame keeps working unchanged).
export interface StemEntry {
  id: StemId
  analyser: StereoAnalyser
}
let stemEntries: StemEntry[] = []
let stemVoicer: StemVoicer | null = null

/** The analysers of a live stem session (a fresh voicer fuses them). */
export function setStemSet(entries: StemEntry[]): void {
  stemEntries = entries
  stemVoicer = new Engine.StemVoicer()
}

/** No stem analysers any more — the source changed. */
export function clearStemSet(): void {
  stemEntries = []
}

// IN-ENGINE SEPARATION status (the auto-separate-on-file-drop pipeline)
export interface SeparationStatus {
  state: 'idle' | 'working' | 'live' | 'failed'
  pct?: number
  phase?: string
}
let sepStatus: SeparationStatus = { state: 'idle' }
const sepListeners = new Set<(s: SeparationStatus) => void>()

function emitSeparation(next: SeparationStatus): void {
  sepStatus = next
  sepListeners.forEach((fn) => fn(next))
}

/** Subscribe to in-engine separation progress; fires immediately with the current status. */
export function onSeparation(fn: (s: SeparationStatus) => void): () => void {
  sepListeners.add(fn)
  fn(sepStatus)
  return () => sepListeners.delete(fn)
}

// THE MUTED PARALLEL STEM SESSION — the hot-swap trick. The user's file keeps playing
// natively, untouched; the separated stems play SILENTLY in their own context, kept in
// sync with the media element, feeding the analysers. Visuals upgrade to instrument
// truth mid-song; the audio path carries zero risk.
let mutedCtx: AudioContext | null = null
let mutedStop: (() => void) | null = null
let mutedPaused = false

function stopMutedStemSession(): void {
  mutedStop?.()
  mutedStop = null
  closeCtx(mutedCtx)
  mutedCtx = null
  mutedPaused = false
}

/** The source changed: the muted stem session (if any) ends and the separation status returns to idle. */
export function stopSeparation(): void {
  stopMutedStemSession()
  emitSeparation({ state: 'idle' })
}

function startMutedStemSession(media: HTMLMediaElement, stems: SeparatedStems): void {
  stopMutedStemSession()
  const ctx = new AudioContext()
  mutedCtx = ctx
  const silent = ctx.createGain()
  silent.gain.value = 0 // graphs only process when they reach the destination — silently
  silent.connect(ctx.destination)

  const buffers: AudioBuffer[] = []
  const gains: GainNode[] = []
  const entries: StemEntry[] = []
  for (let s = 0; s < stems.ids.length; s += 1) {
    const buf = ctx.createBuffer(2, stems.channels[2 * s].length, stems.sampleRate)
    buf.copyToChannel(stems.channels[2 * s], 0)
    buf.copyToChannel(stems.channels[2 * s + 1], 1)
    buffers.push(buf)
    const g = ctx.createGain()
    g.connect(silent)
    gains.push(g)
    const analyser = new Engine.StereoAnalyser()
    analyser.attachSource(ctx, g) // wired ONCE — re-syncs only recreate the buffer sources
    entries.push({ id: stems.ids[s], analyser })
  }

  let sources: AudioBufferSourceNode[] = []
  let anchorCtx = 0
  let anchorMedia = 0
  const resync = (): void => {
    sources.forEach((s) => {
      try {
        s.stop()
      } catch {
        /* never started */
      }
    })
    sources = buffers.map((buf, i) => {
      const src = ctx.createBufferSource()
      src.buffer = buf
      src.connect(gains[i])
      return src
    })
    anchorCtx = ctx.currentTime + 0.06
    anchorMedia = Math.min(media.currentTime, buffers[0].duration - 0.05)
    sources.forEach((s) => s.start(anchorCtx, Math.max(0, anchorMedia)))
  }
  // CRITICAL: this context is born in the async separation callback, far from the file-drop
  // gesture, so Chrome starts it SUSPENDED — its clock frozen, sources never play, analysers
  // read silence. Resume (sticky activation from the drop allows it), THEN schedule the sources
  // against the now-advancing clock.
  void ctx.resume().then(() => {
    resync()
    console.log('[syqel·sep] muted stem session running, ctx.state =', ctx.state)
  })
  resync()

  const onPause = (): void => {
    mutedPaused = true
    void ctx.suspend()
  }
  const onPlay = (): void => {
    mutedPaused = false
    void ctx.resume().then(resync)
  }
  const onSeeked = (): void => {
    if (!mutedPaused) resync()
  }
  media.addEventListener('pause', onPause)
  media.addEventListener('ended', onPause)
  media.addEventListener('play', onPlay)
  media.addEventListener('seeked', onSeeked)
  const drift = window.setInterval(() => {
    if (mutedPaused) return
    const expected = anchorMedia + (ctx.currentTime - anchorCtx)
    if (Math.abs(media.currentTime - expected) > 0.15) resync()
  }, 2000)
  mutedStop = () => {
    window.clearInterval(drift)
    media.removeEventListener('pause', onPause)
    media.removeEventListener('ended', onPause)
    media.removeEventListener('play', onPlay)
    media.removeEventListener('seeked', onSeeked)
  }

  setStemSet(entries)
}

/** Separate the dropped file (playing on `media`) in the background and hot-swap the analysis to
 *  instruments. `current` answers whether that file is still the session's source. */
export async function autoSeparate(file: File, media: HTMLMediaElement, current: () => boolean): Promise<void> {
  try {
    console.log('[syqel·sep] starting separation of', file.name)
    emitSeparation({ state: 'working', pct: 0, phase: 'starting' })
    const stems = await separateFile(file, (pct, phase) => {
      if (current()) emitSeparation({ state: 'working', pct: Math.round(pct), phase })
    })
    if (!current()) return // the user moved on to another source — discard
    console.log('[syqel·sep] separation complete:', stems.ids.join('/'), '| samples:', stems.channels[0]?.length)
    startMutedStemSession(media, stems)
    emitSeparation({ state: 'live' })
    track('stems_separated', { surface: surface() })
  } catch (err) {
    const reason = err instanceof Error ? err.message : String(err)
    console.error('[syqel·sep] separation FAILED:', reason, err)
    if (current()) emitSeparation({ state: 'failed', phase: reason })
  }
}

export function inferStemId(name: string, taken: Set<StemId>): StemId {
  const n = name.toLowerCase()
  const guess: StemId =
    n.includes('vocal') || n.includes('vox') ? 'vocals'
    : n.includes('drum') || n.includes('perc') ? 'drums'
    : n.includes('bass') ? 'bass'
    : n.includes('guitar') ? 'guitar'
    : n.includes('piano') || n.includes('keys') ? 'piano'
    : 'other'
  const id = taken.has(guess) ? 'other' : guess
  taken.add(id)
  return id
}

/**
 * Per-frame isolated-instrument FUSION: 16 instrument-true voices + pan events + per-stem
 * aggregates. The caller swaps these into the MusicalFrame so every world becomes
 * stem-driven with zero world changes. Undefined when no stem session is active.
 */
export function readStems(timeSec: number): StemVoicesFrame | undefined {
  if (stemEntries.length === 0 || !stemVoicer) return undefined
  if (mutedPaused) return undefined // frozen analysers would report stale energy while paused
  const inputs: StemInput[] = []
  for (const e of stemEntries) {
    const ch = e.analyser.readChannels()
    if (ch) inputs.push({ id: e.id, l: ch.l, r: ch.r })
  }
  return inputs.length ? stemVoicer.update(inputs, timeSec) : undefined
}
